/**
 * The evergreen-by-area catalog (NM-30).
 *
 * A static map of `section slug → ranked evergreen topic names`, shown
 * **instantly** when a user drills into a section (or subcategory) in the
 * discover browser — no AI call, no latency, works with no provider configured.
 * It is to the section grid what `profile-topics.ts`'s table is to the
 * onboarding profile picker and the "because you're into…" strip (`forYouJsx`):
 * a pre-populated floor that renders the moment the door opens, on top of which
 * the AI call *appends* the timely/ongoing half when it arrives (FR-24.10,
 * FR-36.11 — the "static floor + AI enhancement" pattern the profile door
 * already uses).
 *
 * **Derived, then hand-tuned.** The entries are drawn from the 240 evergreen
 * beats in `PROFILE_TOPICS` (all evergreen by construction — FR-36.2), grouped
 * from their reader-profile organisation into the taxonomy's *sections*
 * (`BUILTIN_CATEGORIES` in `categories.ts`). Where a section had few or
 * no profile beats — `world`, `media`, `society`, `transport` — a handful were
 * added by hand in the same register, so every visible section opens onto a
 * useful set rather than a near-empty one. This is the first pass; extending or
 * reordering a section is a one-line edit here.
 *
 * **Keyed by the real section slugs** (`slugify(label)` — `world`, `food-drink`,
 * `law-justice`, …), so the catalog stays consistent with the filter bar and the
 * classifier that use the same slugs. A slug with no entry (e.g. `other`) simply
 * has no instant picks, and the drill-in falls back to the AI call alone.
 *
 * **Names, not full suggestions**, exactly like `PROFILE_TOPICS`: the render
 * synthesises the `evergreen` kind and the section at display time. Names are
 * ranked best-first; the caller caps and de-duplicates against topics the user
 * already follows (see {@link evergreenTopicsForSection}).
 */

import { BUILTIN_CATEGORIES, findCategory } from './categories.js';

/**
 * Evergreen topic names per section slug, ranked best-first.
 *
 * Every key is a live section slug in `BUILTIN_CATEGORIES` (asserted by
 * `tests/unit/section-topics.test.ts`); `other` is deliberately absent — the
 * catch-all has no standing beats of its own.
 */
export const SECTION_TOPICS: Readonly<Partial<Record<string, readonly string[]>>> = {
  world: [
    'International relations and diplomacy',
    'Global conflicts and security',
    'Migration and refugees',
    'Global trade and supply chains',
    'Humanitarian crises and aid',
  ],
  politics: [
    'Elections and campaigns',
    'Legislation and policy',
    'Government budgets and spending',
    'Political parties and leadership',
    'Polling and public opinion',
  ],
  business: [
    'Corporate earnings',
    'Mergers and acquisitions',
    'Consumer spending trends',
    'Remote and hybrid work',
    'Small business and entrepreneurship',
  ],
  money: [
    'Stock markets and indices',
    'Central bank policy and interest rates',
    'Inflation and economic data',
    'Personal budgeting and saving',
    'Mortgage rates and housing costs',
    'Retirement income and pensions',
  ],
  technology: [
    'Artificial intelligence',
    'Consumer gadgets and product launches',
    'Smartphones and mobile platforms',
    'Computer chips and semiconductors',
    'Big Tech antitrust and regulation',
    'Cloud platforms and infrastructure',
  ],
  science: [
    'Space exploration and astronomy',
    'Physics and fundamental research',
    'Biology and genetics research',
    'Archaeology and human origins',
    'Research funding and scientific publishing',
  ],
  environment: [
    'Climate science',
    'Renewable energy transition',
    'Extreme weather and adaptation',
    'Climate policy and agreements',
    'Conservation and biodiversity',
  ],
  health: [
    'Medical research and clinical trials',
    'Drug approvals and pharmaceuticals',
    'Nutrition and diet research',
    'Mental health and treatment',
    'Healthcare policy and funding',
    'Exercise science and fitness',
  ],
  sports: [
    'League results and standings',
    'Transfers, trades and signings',
    'Title races and playoffs',
    'Athlete injuries and comebacks',
    'Sports business and broadcast rights',
  ],
  entertainment: [
    'Film releases and reviews',
    'Streaming platforms and originals',
    'Awards season',
    'Video game releases and reviews',
    'New album releases',
    'Box office and studio business',
  ],
  media: [
    'Journalism and press freedom',
    'Publishing industry',
    'Social platforms and content moderation',
    'Advertising and marketing',
    'Podcasts and audio',
  ],
  culture: [
    'New book releases',
    'Literary prizes and awards',
    'Museums and exhibitions',
    'Art world and the market',
    'History and new discoveries',
  ],
  'food-drink': [
    'Restaurant openings and chef news',
    'Food trends and ingredients',
    'Recipes and techniques',
    'Food safety and recalls',
    'Wine, beer and spirits',
  ],
  travel: [
    'Airline routes and fares',
    'Travel disruption and airport operations',
    'Visa rules and border requirements',
    'Hotels and short-term rentals',
    'Destinations and overtourism',
  ],
  style: [
    'Runway shows and collections',
    'Fashion industry business',
    'Sustainable and secondhand fashion',
    'Beauty product launches',
    'Skincare science and ingredients',
  ],
  living: [
    'Home renovation and DIY',
    'Gardening and seasonal planting',
    'Pet health and veterinary medicine',
    'Home energy efficiency',
    'Photography gear and technique',
  ],
  education: [
    'Education policy and funding',
    'University admissions and entrance exams',
    'Teaching practice and curriculum',
    'AI and technology in the classroom',
    'Language learning and study tools',
  ],
  'law-justice': [
    'Court rulings and legal precedent',
    'Regulation and compliance',
    'Crime and policing',
    'Legal industry and law firms',
    'Legal technology and AI in law',
  ],
  society: [
    'Family, childcare and parenting policy',
    'Nonprofit sector and charities',
    'Aging and longevity',
    'Housing and homelessness',
    'Immigration and communities',
  ],
  transport: [
    'Electric vehicles and batteries',
    'Auto industry and manufacturing',
    'Commercial aviation industry',
    'Air safety and incident investigations',
    'Public transit and infrastructure',
  ],
};

/** Normalised comparison key for de-duplication — mirrors `profile-topics`. */
function normalizeTopicName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * The instant evergreen picks for a section, ranked best-first, capped, and with
 * any names the user already follows removed.
 *
 * Returns `[]` for a slug with no catalog entry (e.g. `other`, or an unknown
 * slug), so the caller shows nothing rather than throwing — the drill-in then
 * relies on the AI call alone.
 */
export function evergreenTopicsForSection(
  slug: string,
  opts: { cap?: number; exclude?: readonly string[] } = {},
): string[] {
  const names = SECTION_TOPICS[slug];
  if (names === undefined) return [];
  const seen = new Set((opts.exclude ?? []).map(normalizeTopicName));
  const out: string[] = [];
  for (const name of names) {
    const key = normalizeTopicName(name);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
    if (opts.cap !== undefined && out.length >= opts.cap) break;
  }
  return out;
}

/** Whether a section has any instant evergreen picks — used to decide the strip. */
export function hasEvergreenTopics(slug: string): boolean {
  const names = SECTION_TOPICS[slug];
  return names !== undefined && names.length > 0;
}

/**
 * Every catalog key must be a live (non-retired) section slug. Exported for the
 * unit test rather than computed there, so the invariant lives beside the data.
 */
export function unknownSectionSlugs(): string[] {
  return Object.keys(SECTION_TOPICS).filter((slug) => {
    const cat = findCategory(BUILTIN_CATEGORIES, slug);
    return cat === undefined || cat.retired;
  });
}
